import type { Goal } from '@medplum/fhirtypes';

/**
 * Goal clinical-progress + primary-flag helpers.
 *
 * Two axes that must NEVER be conflated with the review axis
 * (`lifecycleStatus`, owned by the findings-review transitions):
 *  - done / not-done ↔ `achievementStatus` (HL7 goal-achievement terminology)
 *  - PRIMARY (one per plan, app-enforced by the write path) ↔
 *    `priority: high-priority` (HL7 goal-priority terminology)
 */

export const GOAL_ACHIEVEMENT_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/goal-achievement';
export const GOAL_PRIORITY_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/goal-priority';

export type GoalAchievement = 'achieved' | 'in-progress';

const ACHIEVEMENT_DISPLAY: Record<GoalAchievement, string> = {
  achieved: 'Achieved',
  'in-progress': 'In Progress',
};

export const setGoalAchievement = (
  goal: Goal,
  achievement: GoalAchievement,
): Goal => ({
  ...goal,
  achievementStatus: {
    coding: [
      {
        system: GOAL_ACHIEVEMENT_SYSTEM,
        code: achievement,
        display: ACHIEVEMENT_DISPLAY[achievement],
      },
    ],
  },
});

export const goalAchievement = (goal: Goal): string | undefined =>
  goal.achievementStatus?.coding?.find(
    (c) => c.system === GOAL_ACHIEVEMENT_SYSTEM,
  )?.code;

export const isGoalDone = (goal: Goal): boolean =>
  goalAchievement(goal) === 'achieved';

export const setGoalPrimary = (goal: Goal, primary: boolean): Goal => {
  if (!primary) {
    const rest = { ...goal };
    delete rest.priority;
    return rest;
  }
  return {
    ...goal,
    priority: {
      coding: [
        {
          system: GOAL_PRIORITY_SYSTEM,
          code: 'high-priority',
          display: 'High Priority',
        },
      ],
    },
  };
};

export const isPrimaryGoal = (goal: Goal): boolean =>
  (goal.priority?.coding ?? []).some(
    (c) => c.system === GOAL_PRIORITY_SYSTEM && c.code === 'high-priority',
  );
